import { useEffect, useState, useRef } from 'react';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Skeleton } from 'primereact/skeleton';
import { Columns3Cog, FileClock } from 'lucide-react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { Toast } from 'primereact/toast';

import { VALID_MEASURES } from '../../../constants/measures.constant';
import { useAlerts } from '../../shared/Alerts/alertsService';
import { getDashboardList } from '../Dashboard/services/Dashboards.service';
import { checkUrlString, isWarrantyEnabled } from '../../../utils/Helpers';
import { CLIENT_ID } from '../../../constants/client.constant';
import PageHeaderBreadcrumb from '../../shared/PageHeader/PageHeaderBreadcrumb';
import { RandomCard } from '../Dashboard/components/RandomCards';
import { formAction, MODULES } from '../../../constants';
import { buildWidgetFreezeTableKey } from '../../shared/PFPrime/PFFreezeDataTable';
import { encrypt } from '../../../utils/security.helper';

import ActionButtons from './components/ActionButtons';
import BasicSection from './components/BasicSection';
import WidgetDetails from './components/WidgetDetails';
import WidgetRenderer from './components/WidgetRenderer';
import WidgetTableRenderer from './components/WidgetTableRenderer';
import WidgetTypeFilter from './components/WidgetTypeFilter';
import ButtonWidget from './components/ButtonWidget';
import {
  getDataSource,
  getQueryResult,
  createWidget,
  getWidgetDataById,
  editWidget,
  getFilterDetails,
} from './service/widget.service';
import {
  getChartQuery,
  getTableQuery,
  mapResponseToFormik,
  prepareQueryWithFilters,
  prepareChartQueryWithFilters,
  routingWidgetDefaultCondition,
  getCompareToQuery,
} from './helpers/query';
import {
  getWidgetFieldsValidationSchema,
  widgetsFormInitialValues,
} from './helpers/widget-form-validations';
import WidgetGroupedTableRenderer from './components/WidgetGroupedTableRenderer';
import { CUBE_LABELS } from './constant/constant';
import ThirdPartyApiConfiguration from './components/ThirdPartyApiConfiguration';
import FilterPanel from './components/FilterPanel';
import ReportConfiguration from './components/ReportConfiguration';
import ReportPanel from './components/ReportPanel';
import QuickFiltersPanel from './components/QuickFiltersPanel';
import CloneWidget from './components/CloneWidget';

const initialOrderBy = {
  columns: '',
  direction: 1,
};
const WidgetContainerNew = () => {
  const history = useHistory();
  const { setAlert } = useAlerts();
  const action = window.location.pathname.split('/')[3];
  const widgetId = window.location.pathname.split('/')[4];
  const createWidgetTemplateBreadcrumb = [
    {
      label: 'Reports & Logs',
      icon: FileClock,
    },
    {
      label: 'Reports',
    },
    {
      url: '/admin/widgets',
      label: 'Widgets',
    },
    {
      url: `/admin/widgets/${action !== 'create' ? action + '/' + widgetId : 'create'}`,
      label: 'Widget Details',
    },
  ];

  const [dimensionList, setDimensionList] = useState([]);
  const [chartMeasures, setChartMeasures] = useState([]);
  const [tableMeasures, setTableMeasures] = useState([]);

  const [tableList, setTableList] = useState([]);
  const [cubeResponse, setCubeResponse] = useState([]);
  const [compareToResponse, setCompareToResponse] = useState([]);
  const [cubeTableResponse, setCubeTableResponse] = useState([]);
  const [tablePagination, setTablePagination] = useState({
    filters: null,
  });
  const [paginator, setPaginator] = useState({
    first: 0,
    currentPage: 0,
    perPage: 10,
  });
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [totalCount, setTotalCount] = useState(0);
  const [cubeQuery, setCubeQuery] = useState({});
  const [loading, setLoading] = useState(false);
  const [hidePreview, setHidePreview] = useState(true);

  const [selectedTable, setSelectedTable] = useState('Project');
  const [showChartFilter, setShowChartFilter] = useState(false);

  const [showTableFilter, setShowTableFilter] = useState(false);

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [dashboardListData, setDashboardListData] = useState({});
  const [selectedChartTable, setSelectedChartTable] = useState('Project');
  const [filterDetails, setFilterDetails] = useState(null);
  const [isCopyUse, setIsCopyUse] = useState(true);
  const [visible, setVisible] = useState(false);
  const [showButtonPreview, setShowButtonPreview] = useState(false);
  const clientId = localStorage.getItem('client_id');
  const toast = useRef(null);
  const [showChartQuickFilters, setShowChartQuickFilters] = useState(false);
  const [showReportQuickFilters, setShowReportQuickFilters] = useState(false);
  const [cloneWidgetDialog, setCloneWidgetDialog] = useState({
    isVisible: false,
    initialValues: null,
  });

  const hasMatchingPrimaryKeyWithAddField = (table_configuration = []) => {
    return table_configuration?.some(tableItem =>
      dimensionList?.some(
        dimension =>
          dimension.primaryKey === tableItem.measureName &&
          dimension.meta?.addField
      )
    );
  };
  const widgetFormik = useFormik({
    initialValues: widgetsFormInitialValues,
    validationSchema: getWidgetFieldsValidationSchema(
      dimensionList,
      selectedTable
    ),
    onSubmit: async values => {
      setSaveLoading(true);
      if (values.is_third_party === 1) {
        values = {
          ...values,
          report_group_by: null,
          report_group_by_aggr: null,
          chart_configuration: {
            ...values.chart_configuration,
            groupBy: [],
          },
        };
        fetchData();
      }
      // Temporary safeguard fix:
      // When cloning widgets, some records were created with incorrect `additional_dimensions`
      // inside `custom_properties`. This check ensures that if no valid primary key
      // with `meta.addField` exists, we reset `additional_dimensions` to prevent
      // inconsistent data behavior.
      // This avoids running a DB correction script and safely fixes the issue at runtime.
      const isExist = hasMatchingPrimaryKeyWithAddField(
        values?.table_configuration
      );
      if (!isExist) {
        values = {
          ...values,
          custom_properties: {
            ...values.custom_properties,
            additional_dimensions: [],
          },
        };
      }

      if (action === 'edit') {
        const response = await editWidget(values, widgetId);
        if (response.status) {
          setAlert('success', 'Widget updated successfully', false, true);
          history.push('/admin/widgets');
        }
      }
      if (action === 'create') {
        const response = await createWidget(values);
        if (response.status) {
          setAlert('success', 'Widget created successfully', false, true);
          history.push('/admin/widgets');
        }
      }
      setSaveLoading(false);
    },
    enableReinitialize: true,
  });

  const copyChartSegments = async () => {
    await widgetFormik.setFieldValue('custom_properties', {
      ...widgetFormik.values.custom_properties,
      quick_filter_report_conditions: JSON.parse(
        JSON.stringify(
          widgetFormik.values.custom_properties?.quick_filter_chart_conditions
        )
      ),
    });
    setShowReportQuickFilters(true);
  };
  const copyReportSegments = async () => {
    await widgetFormik.setFieldValue('custom_properties', {
      ...widgetFormik.values.custom_properties,
      quick_filter_chart_conditions: JSON.parse(
        JSON.stringify(
          widgetFormik.values.custom_properties?.quick_filter_report_conditions
        )
      ),
    });
    setShowChartQuickFilters(true);
  };

  const routingAcceptHandler = () => {
    setShowChartFilter(false);
    setHidePreview(true);
    setSelectedChartTable('Project');
    setSelectedTable('Project');
    updateTableFilter({
      ...widgetFormik.values,
      custom_properties: {
        ...widgetFormik.values.custom_properties,
        selectedReportTable: 'Project',
        reportTablePrimaryKey: 'Project.project_id',
        selectedChartTable: 'Project',
      },
    });
  };
  const routingRejectHandler = () => {
    widgetFormik.setFieldValue('route_widget', 0);
  };
  const setRoutingWidgetHandler = isRouting => {
    if (isRouting) {
      if (selectedChartTable != 'Project' || selectedTable != 'Project') {
        setVisible(true);
      }
    }
  };

  const setSelectedChartTableValue = (chartTableValue, formikData) => {
    setSelectedChartTable(chartTableValue);
    const newValues = {
      ...formikData,
      chart_filter_conditions: {
        condition: 'and',
        filters: [
          {
            condition: 'and',
            fields: [{ tableField: '', operator: '', value: '' }],
          },
        ],
      },
      chart_configuration: {
        dimension: '',
        measure: '',
        measureSummarizedBy: '',
        measureTarget: 100,
        groupBy: [],
      },
    };
    widgetFormik.setValues(newValues);
    setShowChartFilter(false);
    setHidePreview(true);
  };

  const updateTableFilter = formikData => {
    const newValues = {
      ...formikData,
      table_configuration: [{ measureName: '', alice: '' }],
      table_filter_conditions: {
        condition: 'and',
        filters: [
          {
            condition: 'and',
            fields: [{ tableField: '', operator: '', value: '' }],
          },
        ],
      },
      order_by: {
        orderField: '',
        orderBy: 'asc',
      },
    };
    widgetFormik.setValues(newValues);
    setShowTableFilter(false);
    setHidePreview(true);
  };

  const fetchQubeJsData = async () => {
    const response = await getDataSource();

    const filteredCubes = response?.filter(cube => {
      if (
        cube?.title === CUBE_LABELS.USER_DETAILS &&
        cube?.name !== CUBE_LABELS.USER_META
      ) {
        return false;
      } else {
        return true;
      }
    });

    const formattedDimensions = [];
    const tableList = [];
    if (Array.isArray(filteredCubes))
      filteredCubes.forEach(option => {
        const primaryKeyMeasure = option.dimensions.find(
          item => item.meta && item.meta.primaryKey
        );
        tableList.push({
          key: option.name,
          value: option.title || option.name,
          title: option.title || option.name,
          primaryKey: primaryKeyMeasure ? primaryKeyMeasure.name : undefined,
        });
        option.dimensions.forEach(measureItem => {
          if (!measureItem?.meta?.foreignKeyField) {
            formattedDimensions.push({
              firstLetter: option.title || option.name,
              key: measureItem.name,
              value: measureItem.shortTitle,
              primaryKey: primaryKeyMeasure
                ? primaryKeyMeasure.name
                : undefined,
              primaryKeyMeasures: primaryKeyMeasure
                ? primaryKeyMeasure.measures
                : [],
              ...measureItem,
            });
          }
        });
      });

    setTableList(tableList);
    setDimensionList(formattedDimensions);
  };

  useEffect(async () => {
    if (action === 'create') {
      fetchQubeJsData();
    }
    const filterResponse = await getFilterDetails();
    setFilterDetails(filterResponse);
    return;
  }, []);

  useEffect(() => {
    if (Array.isArray(dimensionList) && dimensionList.length) {
      if (VALID_MEASURES[selectedChartTable]) {
        const selectedChartTableDimensions = dimensionList.filter(
          ({ firstLetter, name }) =>
            selectedChartTable === firstLetter &&
            VALID_MEASURES[selectedChartTable].includes(name)
        );
        setChartMeasures(selectedChartTableDimensions);
      } else {
        const selectedTableObj = tableList.find(
          item =>
            item.value === selectedChartTable || item.key === selectedChartTable
        );

        const regex = new RegExp(`^Entity_${clientId.toLowerCase()}_`);
        if (regex.test(selectedTableObj?.key)) {
          const selectedChartTableDimensions = dimensionList.filter(
            ({ firstLetter, type }) =>
              selectedChartTable === firstLetter && type === 'number'
          );
          setChartMeasures(selectedChartTableDimensions);
        }
      }
    }
  }, [selectedChartTable, dimensionList]);
  useEffect(() => {
    if (Array.isArray(dimensionList) && dimensionList.length) {
      if (VALID_MEASURES[selectedTable]) {
        const selectedTableDimensions = dimensionList.filter(
          ({ firstLetter, name }) =>
            selectedTable === firstLetter &&
            VALID_MEASURES[selectedTable].includes(name)
        );
        setTableMeasures(selectedTableDimensions);
      }
    }
  }, [selectedTable, dimensionList]);

  const fetchData = async () => {
    if (
      widgetFormik.values.widget_type === 'external-link-button' &&
      [
        'urlWidget',
        'dashboardWidget',
        'readOnlyWidget',
        'tabularWidget',
      ].includes(widgetFormik?.values?.custom_properties?.buttonWidgetType)
    ) {
      setShowButtonPreview(true);
    } else {
      if (
        (widgetFormik.values.widget_type === 'external-link-button' &&
          ['tabularWidget', 'routingWidget'].includes(
            widgetFormik?.values?.custom_properties?.buttonWidgetType
          )) ||
        widgetFormik.values.widget_type === 'card' ||
        widgetFormik.values.widget_type === 'progress-bar'
      ) {
        setShowButtonPreview(true);
      }

      setTablePagination({
        filters: null,
      });
      setPaginator({ first: 0, currentPage: 0, perPage: 10 });
      setLoading(true);
      const apiList = [fetchTableData()];
      if (widgetFormik.values.widget_type != 'external-link-button') {
        apiList.push(fetchChartData());
      }

      if (
        widgetFormik?.values?.custom_properties?.performanceType ===
        'periodComparison'
      ) {
        apiList.push(fetchCompareToData());
      }
      Promise.all(apiList).then(() => {
        setLoading(false);
      });
    }
    setHidePreview(false);
  };

  useEffect(() => {
    if (hidePreview) {
      setIsDataLoaded(true);
      return;
    }
    Promise.all([fetchTableData(tablePagination, orderBy)]).then(() => {
      setLoading(false);
    });
  }, [tablePagination]);
  const prevValues = useRef(widgetFormik.values);

  useEffect(() => {
    const prev = { ...prevValues.current };
    const current = { ...widgetFormik.values };

    delete prev.order_by;
    delete current.order_by;

    if (JSON.stringify(prev) !== JSON.stringify(current)) {
      setHidePreview(true);
    }

    prevValues.current = widgetFormik.values;
  }, [widgetFormik.values]);

  useEffect(() => {
    if (hidePreview) {
      setIsDataLoaded(true);
      return;
    }
    Promise.all([fetchTableData(tablePagination, orderBy)]).then(() => {
      setLoading(false);
    });
  }, [orderBy, paginator]);

  const fetchChartData = async () => {
    const formattedQuery = getChartQuery(widgetFormik.values);
    const query = prepareChartQueryWithFilters({
      query: widgetFormik.values,
      formattedQuery,
      filterDetails,
    });
    setCubeQuery(query);
    if (widgetFormik?.values?.route_widget) {
      query.filters.push(routingWidgetDefaultCondition());
    }
    const isSupportTicketChartTable =
      selectedChartTable?.toLocaleLowerCase() ===
      MODULES.SUPPORT.MAIN_CUBE_NAME_LOWERCASE;

    const response = await getQueryResult(
      query,
      widgetFormik.values,
      isSupportTicketChartTable,
      widgetId !== undefined && widgetId !== null && !isNaN(Number(widgetId))
        ? encrypt(widgetId)
        : undefined
    );
    setCubeResponse(response);
    setHidePreview(false);
  };

  const fetchCompareToData = async () => {
    const formattedQuery = getCompareToQuery(widgetFormik.values);

    const query = prepareChartQueryWithFilters({
      query: widgetFormik.values,
      formattedQuery,
      filterDetails,
    });

    if (widgetFormik?.values?.route_widget) {
      query.filters.push(routingWidgetDefaultCondition());
    }

    const isSupportTicketChartTable =
      selectedChartTable?.toLocaleLowerCase() ===
      MODULES.SUPPORT.MAIN_CUBE_NAME_LOWERCASE;

    const response = await getQueryResult(
      query,
      widgetFormik.values,
      isSupportTicketChartTable,
      widgetId !== undefined && widgetId !== null && !isNaN(Number(widgetId))
        ? encrypt(widgetId)
        : undefined
    );

    setCompareToResponse(response);
    setHidePreview(false);
  };

  const onTableChangeHandler = params => {
    setTablePagination(params);
  };

  const fetchTableData = async (
    params = tablePagination,
    orderByConfiguration = orderBy
  ) => {
    setIsDataLoaded(false);
    const formattedQuery = getTableQuery(widgetFormik.values);
    const query = prepareQueryWithFilters({
      tablePagination: params,
      query: widgetFormik.values,
      formattedQuery,
      cubeResponse: cubeTableResponse,
      filterDetails: filterDetails,
      orderByConfiguration,
      paginator,
    });
    query.total = true;
    const isSupportTicketTable =
      selectedTable?.toLocaleLowerCase() ===
      MODULES.SUPPORT.MAIN_CUBE_NAME_LOWERCASE;

    const response = await getQueryResult(
      query,
      null,
      isSupportTicketTable,
      widgetId !== undefined && widgetId !== null && !isNaN(Number(widgetId))
        ? encrypt(widgetId)
        : undefined
    );
    setIsDataLoaded(true);
    setCubeTableResponse(response);
    setTotalCount(response?.total);
  };

  const fetchWidgetDetails = async () => {
    setSaveLoading(true);
    await fetchQubeJsData();
    const response = await getWidgetDataById(widgetId);
    const formikValues = mapResponseToFormik(response);

    if (formikValues?.widget_type === 'gauge') {
      formikValues.custom_properties = {
        ...formikValues.custom_properties,
        showPerformance: true,
        performanceType:
          formikValues?.chart_configuration?.measureTarget && 'fixedTarget',
      };
    }

    setSelectedTable(formikValues.custom_properties.selectedReportTable);
    setSelectedChartTable(formikValues.custom_properties.selectedChartTable);
    widgetFormik.setValues(formikValues, false);

    if (
      formikValues?.chart_filter_conditions?.filters?.[0]?.fields?.[0]
        ?.tableField
    ) {
      setShowChartFilter(true);
    }
    if (
      formikValues?.table_filter_conditions?.filters?.[0]?.fields?.[0]
        ?.tableField
    ) {
      setShowTableFilter(true);
    }
    if (
      formikValues?.custom_properties?.quick_filter_chart_conditions?.[0]
        ?.tableField
    ) {
      setShowChartQuickFilters(true);
    }
    if (
      formikValues?.custom_properties?.quick_filter_report_conditions?.[0]
        ?.tableField
    ) {
      setShowReportQuickFilters(true);
    }
    setSaveLoading(false);
  };

  const getDashBoardData = () => {
    getDashboardList('', () => {}, setDashboardListData);
  };

  useEffect(() => {
    getDashBoardData();
    if (action === 'create') {
      return;
    }
    fetchWidgetDetails();
  }, []);

  const copyFilter = () => {
    widgetFormik.setFieldValue(
      'chart_filter_conditions',
      JSON.parse(JSON.stringify(widgetFormik.values.table_filter_conditions))
    );
    setShowChartFilter(true);
    toast.current?.show({
      severity: 'success',
      summary: 'Copied',
      detail: 'Filters copied successfully',
      life: 3000,
    });
    setIsCopyUse(!isCopyUse);
  };
  const copyFilterChart = () => {
    widgetFormik.setFieldValue(
      'table_filter_conditions',
      JSON.parse(JSON.stringify(widgetFormik.values.chart_filter_conditions))
    );
    setShowTableFilter(true);
    toast.current?.show({
      severity: 'success',
      summary: 'Copied',
      detail: 'Filters copied successfully',
      life: 3000,
    });
    setIsCopyUse(!isCopyUse);
  };
  const onEdit = () => {
    const widgetId = window.location.pathname.split('/')[4];
    history.push(`/admin/widget/edit/${widgetId}`);
  };
  const user_id = localStorage.getItem('user_id');
  const checkEditPermission = obj => {
    if (!obj.editable) {
      return true;
    }

    if (obj.owner_user_id === user_id) {
      return true;
    }
    return false;
  };
  const currentPath = window?.location?.href;

  const handleCancel = () => {
    const matched = checkUrlString(currentPath, 'source=dashboard');
    if (matched === true) {
      window.open('/dashboard', '_self', 'noopener,noreferrer');
    } else {
      history.goBack();
    }
  };
  const handleSaveAsClick = () => {
    const dashboardId = new URLSearchParams(window.location.search).get('id');
    setCloneWidgetDialog({
      isVisible: true,
      initialValues: widgetFormik.values,
      dashboardId: dashboardId,
    });
  };
  const buttonArray = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      outlined: false,
      size: 'small',
      action: onEdit,
      visible:
        action === 'view' &&
        checkEditPermission(widgetFormik.values) &&
        !saveLoading,
    },
    {
      label: 'Cancel',
      outlined: false,
      size: 'small',
      action: handleCancel,
    },
    {
      label: 'Save As',
      outlined: false,
      size: 'small',
      action: handleSaveAsClick,
      visible:
        checkUrlString(currentPath, 'source=dashboard') &&
        action === formAction.EDIT,
      disabled: !widgetFormik?.dirty || !widgetFormik?.isValid,
    },
    {
      label: 'Save',
      outlined: false,
      size: 'small',
      action: widgetFormik.handleSubmit,
      visible: action === 'create' || action === 'edit',
      disabled: !widgetFormik?.dirty || !widgetFormik?.isValid,
    },
  ];
  const headerTemplate = () => {
    return (
      <div className="flex align-items-center gap-2 text-sm font-medium text-gray-700">
        <Columns3Cog className="text-pink-500 text-sm w-1rem h-1rem" />

        <span className="text-sm font-bold text-gray-700">
          Additional Widget Configuration
        </span>
      </div>
    );
  };
  const updateOrderBy = valueOrUpdater => {
    setOrderBy(prev => {
      const next =
        typeof valueOrUpdater === 'function'
          ? valueOrUpdater(prev)
          : valueOrUpdater;
      if (next?.columns) {
        widgetFormik.setFieldValue('order_by', {
          orderField: next.columns,
          orderBy: next.direction > 0 ? 'asc' : 'desc',
        });
      }
      return next;
    });
  };

  useEffect(() => {
    const of = widgetFormik.values.order_by?.orderField;
    const ob = widgetFormik.values.order_by?.orderBy;
    if (of) {
      setOrderBy(prev => {
        const direction = ob === 'desc' ? -1 : 1;
        if (prev.columns === of && prev.direction === direction) return prev;
        return { columns: of, direction };
      });
    }
  }, [
    widgetFormik.values.order_by?.orderField,
    widgetFormik.values.order_by?.orderBy,
  ]);

  return (
    <div className="grid grid-nogutter w-full">
      <Toast ref={toast} />
      <div className="grid grid-nogutter w-full">
        <PageHeaderBreadcrumb
          headerValue="Widget Details"
          breadcrumbs={createWidgetTemplateBreadcrumb}
          buttonArray={buttonArray}
          identifier="widgets"
        />
      </div>
      {cloneWidgetDialog?.isVisible ? (
        <CloneWidget
          cloneWidgetDialog={cloneWidgetDialog}
          setCloneWidgetDialog={setCloneWidgetDialog}
          hasMatchingPrimaryKeyWithAddField={hasMatchingPrimaryKeyWithAddField}
          handleCancel={handleCancel}
        />
      ) : null}

      <ActionButtons
        action={action}
        widgetFormik={widgetFormik}
        dashboardListData={dashboardListData}
        saveLoading={saveLoading}
      />

      <>
        <BasicSection
          action={action}
          widgetFormik={widgetFormik}
          setHidePreview={setHidePreview}
          loading={saveLoading}
        />

        {(clientId === CLIENT_ID.INCREDIBLE ||
          clientId === CLIENT_ID.PF_VALIDATION ||
          clientId === CLIENT_ID.CFI_INSTALLATION) &&
          ['table-report'].includes(widgetFormik.values.widget_type) &&
          widgetFormik.values.widget_type &&
          widgetFormik.values.widget_name && (
            <>
              {saveLoading ? (
                <Skeleton className="w-full" height="45px" />
              ) : (
                <ThirdPartyApiConfiguration
                  action={action}
                  widgetFormik={widgetFormik}
                  setRoutingWidgetHandler={setRoutingWidgetHandler}
                />
              )}
            </>
          )}
        {widgetFormik.values.widget_type && widgetFormik.values.widget_name ? (
          <Accordion className="w-full" activeIndex={0}>
            <AccordionTab
              header={headerTemplate}
              disabled={
                !widgetFormik.values.widget_type ||
                !widgetFormik.values.widget_name
              }
            >
              <div className="grid grid-nogutter w-full gap-2">
                {widgetFormik.values.widget_type &&
                  widgetFormik.values.widget_name && (
                    <>
                      {saveLoading ? (
                        <Skeleton className="w-full" height="45px" />
                      ) : (
                        <WidgetTypeFilter
                          action={action}
                          widgetFormik={widgetFormik}
                        />
                      )}

                      {['external-link-button', 'card'].includes(
                        widgetFormik.values.widget_type
                      ) && (
                        <ButtonWidget
                          action={action}
                          widgetFormik={widgetFormik}
                          setRoutingWidgetHandler={setRoutingWidgetHandler}
                        />
                      )}
                      {![
                        'table-report',
                        'button-report',
                        'tabular-report',
                        'external-link-button',
                      ].includes(widgetFormik.values.widget_type) && (
                        <>
                          <WidgetDetails
                            dimensionList={dimensionList?.filter(item => {
                              let flag = true;
                              if (item?.meta?.tenantPermission) {
                                flag = isWarrantyEnabled();
                              }
                              return (
                                (item?.type === 'string' || item?.primaryKey) &&
                                item?.firstLetter === selectedChartTable &&
                                !item?.meta?.hideInDimension &&
                                flag
                              );
                            })}
                            comparisonDimensionList={dimensionList?.filter(
                              item => {
                                let flag = true;
                                if (item?.meta?.tenantPermission) {
                                  flag = isWarrantyEnabled();
                                }
                                return (
                                  item?.firstLetter === selectedChartTable &&
                                  !item?.meta?.hideInFilter &&
                                  flag
                                );
                              }
                            )}
                            measureList={chartMeasures}
                            action={action}
                            widgetFormik={widgetFormik}
                            tableList={tableList}
                            setSelectedChartTableValue={
                              setSelectedChartTableValue
                            }
                            selectedChartTable={selectedChartTable}
                          />
                          <FilterPanel
                            selectedTable={selectedTable}
                            selectedChartTable={selectedChartTable}
                            action={action}
                            showChartFilter={showChartFilter}
                            setShowChartFilter={setShowChartFilter}
                            copyFilter={copyFilter}
                            widgetFormik={widgetFormik}
                            dimensionList={dimensionList?.filter(item => {
                              let flag = true;
                              if (item?.meta?.tenantPermission) {
                                flag = isWarrantyEnabled();
                              }
                              return (
                                item?.firstLetter === selectedChartTable &&
                                !item?.meta?.hideInFilter &&
                                flag
                              );
                            })}
                            isWarrantyEnabled={isWarrantyEnabled}
                            isCopyUse={isCopyUse}
                          />
                          {['Project'].includes(selectedChartTable) ? (
                            <QuickFiltersPanel
                              isForChart={true}
                              selectedTable={selectedTable}
                              selectedChartTable={selectedChartTable}
                              action={action}
                              showQuickFilters={showChartQuickFilters}
                              setShowQuickFilters={setShowChartQuickFilters}
                              copyFilter={copyReportSegments}
                              widgetFormik={widgetFormik}
                              dimensionList={dimensionList?.filter(item => {
                                let flag = true;
                                if (item?.meta?.tenantPermission) {
                                  flag = isWarrantyEnabled();
                                }
                                return (
                                  item?.firstLetter === selectedChartTable &&
                                  !item?.meta?.hideInFilter &&
                                  flag &&
                                  item?.meta?.useForComparison
                                );
                              })}
                              quickFilterFor="quick_filter_chart_conditions"
                            />
                          ) : null}
                        </>
                      )}

                      {(![
                        'button-report',
                        'external-link-button',
                        'card',
                      ].includes(widgetFormik.values.widget_type) ||
                        ['tabularWidget', 'routingWidget'].includes(
                          widgetFormik?.values?.custom_properties
                            ?.buttonWidgetType
                        )) && (
                        <>
                          <ReportConfiguration
                            tableList={tableList}
                            widgetFormik={widgetFormik}
                            setSelectedTable={setSelectedTable}
                            updateTableFilter={updateTableFilter}
                            selectedTable={selectedTable}
                            action={action}
                            dimensionList={dimensionList}
                            isWarrantyEnabled={isWarrantyEnabled}
                            tableMeasures={tableMeasures}
                          />
                          <ReportPanel
                            selectedTable={selectedTable}
                            selectedChartTable={selectedChartTable}
                            action={action}
                            showTableFilter={showTableFilter}
                            setShowTableFilter={setShowTableFilter}
                            copyFilterChart={copyFilterChart}
                            widgetFormik={widgetFormik}
                            dimensionList={dimensionList?.filter(item => {
                              let flag = true;
                              if (item?.meta?.tenantPermission) {
                                flag = isWarrantyEnabled();
                              }
                              return (
                                item?.firstLetter === selectedTable &&
                                !item?.meta?.hideInFilter &&
                                flag
                              );
                            })}
                            isWarrantyEnabled={isWarrantyEnabled}
                            isCopyUse={isCopyUse}
                          />
                          {['Project'].includes(selectedChartTable) ? (
                            <QuickFiltersPanel
                              isForChart={false}
                              selectedTable={selectedTable}
                              selectedChartTable={selectedChartTable}
                              action={action}
                              showQuickFilters={showReportQuickFilters}
                              setShowQuickFilters={setShowReportQuickFilters}
                              copyFilter={copyChartSegments}
                              widgetFormik={widgetFormik}
                              dimensionList={dimensionList?.filter(item => {
                                let flag = true;
                                if (item?.meta?.tenantPermission) {
                                  flag = isWarrantyEnabled();
                                }
                                return (
                                  item?.firstLetter === selectedChartTable &&
                                  !item?.meta?.hideInFilter &&
                                  flag &&
                                  item?.meta?.useForComparison
                                );
                              })}
                              quickFilterFor="quick_filter_report_conditions"
                            />
                          ) : null}
                        </>
                      )}
                      <div className="flex justify-content-center align-items-center w-full">
                        <Button
                          label="Live Preview"
                          severity="success"
                          icon="pi pi-eye"
                          iconPos="left"
                          pt={{
                            root: {
                              className:
                                'p-2 px-4 border-round-xl transition-all transition-duration-200 shadow-1 hover:shadow-3',
                            },
                          }}
                          onClick={() => fetchData()}
                          disabled={
                            !widgetFormik?.dirty || !widgetFormik?.isValid
                          }
                        />
                      </div>
                      <div className="grid grid-nogutter w-full">
                        {loading ? (
                          <Skeleton
                            className="mb-2 h-2rem"
                            borderRadius="20px"
                          ></Skeleton>
                        ) : (
                          !hidePreview && (
                            <>
                              <div className="grid grid-nogutter w-full mt-2">
                                {!showButtonPreview && (
                                  <>
                                    <Message
                                      severity="info"
                                      className="w-full"
                                      pt={{
                                        text: { className: 'text-xs' },
                                        root: { className: 'p-2' },
                                      }}
                                      text={
                                        <>
                                          {widgetFormik.values.route_widget ? (
                                            <>
                                              * Completed, closed, rejected and
                                              refunded project will not appear
                                              in this list.
                                            </>
                                          ) : null}
                                          Please ensure that the Widget and
                                          Report Filters are selected to filter
                                          the data.
                                        </>
                                      }
                                    />
                                  </>
                                )}
                              </div>
                              <div className="grid grid-nogutter mt-2 w-full">
                                {(cubeResponse &&
                                  Object.keys(cubeResponse).length > 0 &&
                                  'table-report' !==
                                    widgetFormik.values.widget_type) ||
                                [
                                  'button-report',
                                  'external-link-button',
                                  'card',
                                ].includes(widgetFormik.values.widget_type) ? (
                                  <RandomCard
                                    displayType={
                                      widgetFormik?.values
                                        ?.widget_display_type?.[0]
                                    }
                                    userColor={
                                      widgetFormik?.values?.custom_properties
                                        ?.buttonColor
                                    }
                                    widget={widgetFormik.values}
                                  >
                                    <WidgetRenderer
                                      cubeQuery={cubeQuery}
                                      compareToResponse={
                                        compareToResponse?.data
                                      }
                                      widget_type={
                                        widgetFormik.values.widget_type
                                      }
                                      cubeResponse={cubeResponse?.data}
                                      target={
                                        widgetFormik?.values
                                          ?.chart_configuration?.measureTarget
                                      }
                                      annotation={cubeResponse?.annotation}
                                      formikData={widgetFormik.values}
                                      showButtonPreview={showButtonPreview}
                                    />
                                  </RandomCard>
                                ) : (
                                  <Message
                                    severity="info"
                                    className="w-full"
                                    pt={{
                                      text: { className: 'text-xs' },
                                      root: { className: 'p-2' },
                                    }}
                                    text="Chart Preview not available."
                                  />
                                )}
                              </div>
                            </>
                          )
                        )}
                      </div>
                      {(!['external-link-button'].includes(
                        widgetFormik.values.widget_type
                      ) ||
                        ['tabularWidget', 'routingWidget'].includes(
                          widgetFormik?.values?.custom_properties
                            ?.buttonWidgetType
                        )) && (
                        <div className="grid grid-nogutter w-full">
                          {!isDataLoaded ? (
                            <Skeleton
                              className="my-2 h-2rem"
                              borderRadius="30px"
                            ></Skeleton>
                          ) : (
                            !hidePreview && (
                              <div className="grid grid-nogutter w-full">
                                <Divider />
                                {cubeTableResponse?.data &&
                                cubeTableResponse?.data ? (
                                  widgetFormik.values.report_group_by ? (
                                    <>
                                      <WidgetGroupedTableRenderer
                                        widgetDetails={widgetFormik.values}
                                        data={cubeTableResponse?.data}
                                        tablePagination={tablePagination}
                                        onTableChangeHandler={
                                          onTableChangeHandler
                                        }
                                        annotation={
                                          cubeTableResponse?.annotation
                                            ?.dimensions
                                        }
                                        totalCount={totalCount}
                                        orderBy={orderBy}
                                        setOrderBy={updateOrderBy}
                                        paginator={paginator}
                                        setPaginator={setPaginator}
                                        filterDetails={filterDetails}
                                        freezeTableKey={buildWidgetFreezeTableKey(
                                          widgetFormik.values,
                                          'grouped'
                                        )}
                                      />
                                    </>
                                  ) : (
                                    <WidgetTableRenderer
                                      table_configuration={
                                        widgetFormik.values.table_configuration
                                      }
                                      data={cubeTableResponse.data}
                                      tablePagination={tablePagination}
                                      onTableChangeHandler={
                                        onTableChangeHandler
                                      }
                                      annotation={
                                        cubeTableResponse?.annotation
                                          ?.dimensions
                                      }
                                      totalCount={totalCount}
                                      orderBy={orderBy}
                                      setOrderBy={updateOrderBy}
                                      paginator={paginator}
                                      setPaginator={setPaginator}
                                      freezeTableKey={buildWidgetFreezeTableKey(
                                        widgetFormik.values,
                                        'normal'
                                      )}
                                    />
                                  )
                                ) : (
                                  isDataLoaded && (
                                    <Message
                                      severity="info"
                                      className="w-full"
                                      pt={{
                                        text: { className: 'text-xs' },
                                        root: { className: 'p-2' },
                                      }}
                                      text={'Table Preview not available.'}
                                    />
                                  )
                                )}
                              </div>
                            )
                          )}
                        </div>
                      )}
                      <ConfirmDialog
                        visible={visible}
                        onHide={() => setVisible(false)}
                        message="Routing only available with project table, do you want to change table to project?"
                        header="Confirmation"
                        icon="pi pi-exclamation-triangle"
                        accept={routingAcceptHandler}
                        reject={routingRejectHandler}
                        className="w-11 md:w-6 lg:w-5 pf-confirm-dialog"
                      />
                    </>
                  )}
              </div>
            </AccordionTab>
          </Accordion>
        ) : null}
      </>
    </div>
  );
};

export default WidgetContainerNew;
